<template>
  <div
    :id="id"
    class="phone-number-input"
    :class="disabled && copyable && modelValue ? 'clickable' : ''"
  >
    <el-tooltip
      effect="dark"
      placement="bottom-start"
      :disabled="!phoneInfo"
    >
      <template #content>
        <div v-dompurify-html="phoneInfo" />
      </template>
      <div
        class="phone-number-input-wrapper"
        @click="copyPhoneNumber"
      >
        <span
          v-if="disabled"
          class="phone-add-on"
        >
          <b class="fa fa-phone pr-2" />
          <span v-if="countryCallingCode"> ({{ countryCallingCode }}) </span>
        </span>
        <select
          v-else
          v-model="countryCode"
          class="form-control country-code"
        >
          <option
            v-for="(item, index) in countryCodeList"
            :key="index"
            :value="item.country"
          >
            {{ item.country }} ({{ item.countryCallingCode }})
          </option>
        </select>
        <input
          ref="phoneNumberInput"
          v-model="nationalNumber"
          class="form-control phone"
          autocomplete="nope"
          :class="{
            invalid: !isPhoneNumberValid && !disabled,
            invisible: !show,
          }"
          :disabled="disabled"
          :required="required"
          @paste="onPaste"
          @blur="onBlur"
        >
        <el-tooltip
          v-if="showLiveCall && currentCallingPhoneNumber"
          class="item"
          effect="dark"
          content="Use live phone number"
          placement="top-start"
        >
          <div
            class="live-phone-number text-center"
            :style="{ 'pointer-events': disabled ? 'none' : 'all' }"
            @click="useLiveNumber"
          >
            <i class="fa fa-volume-control-phone" />
          </div>
        </el-tooltip>
      </div>
    </el-tooltip>

    <div class="help-block pt-0">
      <slot name="help-block" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch, onMounted } from 'vue';
import { parsePhoneNumber, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';

import util from '@/Utils/util';
import { currentCallLog } from '@/services/callLogs/callLogsViewModel';

const countryCodeList: any = [
  { id: 1, country: 'US', countryCallingCode: '+1' },
  { id: 2, country: 'CA', countryCallingCode: '+1' },
  { id: 3, country: 'AU', countryCallingCode: '+61' },
];

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },

  id: {
    type: String,
    default: 'customer-phone',
  },

  type: {
    type: String,
    default: 'cleave',
  },

  required: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  show: {
    type: Boolean,
    default: true,
  },

  phoneInfo: {
    type: String,
    default: null,
  },

  copyable: {
    type: Boolean,
    default: false,
  },

  showLiveCall: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'onblur', 'useLiveCallPhoneNumber']);

const countryCode = ref(countryCodeList[0].country);
const nationalNumber = ref('');
const phoneNumberInput: Ref<any> = ref(null);
const isPaste = ref(false);

const isPhoneNumberValid = computed(() => isValidPhoneNumber(props.modelValue || ''));

const countryCallingCode = computed(() => {
  if (!isPhoneNumberValid.value) {
    return countryCodeList[0].countryCallingCode;
  }

  const countryItem = countryCodeList.find((item: any) => item.country === countryCode.value);

  if (!countryItem) {
    return countryCodeList[0].countryCallingCode;
  }

  return countryItem.countryCallingCode;
});

const currentCallingPhoneNumber = computed(() => {
  if (!currentCallLog.value) {
    return '';
  }

  return util.getCallingPhoneNumber(currentCallLog.value);
});

const validatePhoneNumber = () => {
  if (!props.modelValue) {
    nationalNumber.value = '';
    return;
  }

  try {
    const phoneNumberObj = parsePhoneNumber(props.modelValue);
    countryCode.value = phoneNumberObj.country || countryCode.value;
    nationalNumber.value = new AsYouType(countryCode.value as any).input(phoneNumberObj.nationalNumber.toString());
  } catch (err) {
    nationalNumber.value = '';
    countryCode.value = countryCodeList[0].country;
  }
};

const formatPhoneNumberWithCountryCode = () => {
  /*
    there are several cases to copy valid phone number
    1. +17622179646
    2. 17622179646
    3. 7622179646 / (762) 217-9646
  */
  let formattedNumber = '';
  const phoneNumberWithPlus = nationalNumber.value.startsWith('+') ? nationalNumber.value : `+${nationalNumber.value}`;
  const phoneNumberWithCallingCode = `${countryCallingCode.value} ${nationalNumber.value}`;

  if (isValidPhoneNumber(nationalNumber.value)) {
    formattedNumber = nationalNumber.value;
  } else if (isValidPhoneNumber(phoneNumberWithCallingCode)) {
    formattedNumber = phoneNumberWithCallingCode;
  } else if (isValidPhoneNumber(phoneNumberWithPlus)) {
    formattedNumber = phoneNumberWithPlus;
  }

  if (!formattedNumber) {
    return;
  }

  const phoneNumberObj = parsePhoneNumber(formattedNumber);

  countryCode.value = phoneNumberObj.country || countryCode.value;
  nationalNumber.value = phoneNumberObj.nationalNumber;

  emit('update:modelValue', phoneNumberObj.number);
};

const updatePhoneNumber = () => {
  if (!nationalNumber.value) {
    emit('update:modelValue', '');
    return;
  }

  if (isPaste.value) {
    formatPhoneNumberWithCountryCode();
    isPaste.value = false;
    return;
  }

  const countryItem = countryCodeList.find((item: any) => item.country === countryCode.value);
  if (countryItem) {
    const phoneNumber = `${countryItem.countryCallingCode} ${nationalNumber.value}`;

    try {
      const phoneNumberObj = parsePhoneNumber(phoneNumber);
      emit('update:modelValue', phoneNumberObj.number);
    } catch (err) {
      return;
    }
  }
};

const copyPhoneNumber = () => {
  if (!props.disabled) {
    return;
  }

  if (!props.copyable) {
    return;
  }

  if (!props.modelValue) {
    return;
  }

  if (!isValidPhoneNumber(props.modelValue)) {
    return;
  }

  const phoneNumberObj = parsePhoneNumber(props.modelValue);

  if (!phoneNumberObj.number) {
    return;
  }

  util.copyText(phoneNumberObj.number);
};

const onPaste = () => {
  isPaste.value = true;
};

const onBlur = () => {
  emit('onblur');
};

const useLiveNumber = async () => {
  emit('update:modelValue', currentCallingPhoneNumber.value);
  emit('useLiveCallPhoneNumber', currentCallingPhoneNumber.value);
};

watch(() => props.modelValue, validatePhoneNumber);

watch(countryCode, (newValue: string) => {
  nationalNumber.value = new AsYouType(newValue as any).input(nationalNumber.value);
  updatePhoneNumber();
});

watch(nationalNumber, (newValue: string) => {
  nationalNumber.value = new AsYouType(countryCode.value as any).input(newValue);
  updatePhoneNumber();
});

onMounted(validatePhoneNumber);
</script>

<style lang="scss">
.phone-number-input {
  .phone-number-input-wrapper {
    display: flex;
    align-items: center;
    background: #f3f2ee;
    border-radius: 4px;

    .phone-add-on {
      display: flex;
      align-items: center;
      padding: 0 8px;
    }

    .country-code {
      width: 80px;
      height: 40px !important;
      background: #f3f2ee;
      color: #252522;
      padding: 0;
      text-align: center;
      -moz-text-align-last: center;
      text-align-last: center;
      border-right: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    .phone {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      flex: 1;
    }

    .live-phone-number {
      width: 40px;
      cursor: pointer;
      color: #000000;
    }
  }

  &.clickable {
    cursor: pointer;
  }
}
.phone-info-container {
  padding-top: 5px;

  .info-item {
    font-size: 13px;
    margin-bottom: 5px;
    font-weight: bold;

    .info-title {
      color: green;
    }
    .info-value {
      color: #00ffa1;
    }
  }
}
</style>
